import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import driverService from "../../api/services/driverService";
import SafeImage from "../../components/common/SafeImage";
import { getCurrencySymbolFromPhone } from "../../utils/currencyFromPhone";


export default function Details() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    fetchDriverDetails();
  }, [id]);

  useEffect(() => {
    // Enable Bootstrap tooltips
    if (window.bootstrap) {
      const tooltipTriggerList = [].slice.call(
        document.querySelectorAll('[data-bs-toggle="tooltip"]'),
      );
      tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new window.bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }, [driver, uploading]);

  const fetchDriverDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await driverService.getDriverById(id);
      if (result.success) {
        setDriver(result.data);
      } else {
        setError(result.message || "Failed to fetch driver details");
        toast.error(result.message || "Failed to fetch driver details");
      }
    } catch (error) {
      setError("Failed to fetch driver details");
      toast.error("Failed to fetch driver details");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await driverService.updateProfileImage(
        driver.driver_id,
        file,
      );
      if (result.success) {
        toast.success("Profile image updated!");
        setDriver((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            profile_image: result.image_url,
          },
        }));
      } else {
        toast.error(result.message || "Failed to update profile image");
      }
    } catch (error) {
      toast.error("Failed to update profile image");
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const statusClass = status === "active" ? "bg-success" : "bg-danger";
    return (
      <span className={`badge ${statusClass} text-capitalize`}>{status}</span>
    );
  };

  // Helper to get full image URL
  const getProfileImageUrl = (profileImage) => {
    if (!profileImage) return "/avatar-placeholder.png";
    if (profileImage.startsWith("/storage/")) {
      return (
        (import.meta.env.VITE_API_URL || "http://localhost:8000") + profileImage
      );
    }
    return profileImage;
  };

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
        <div
          className="spinner-border text-primary mb-3"
          style={{ width: "3rem", height: "3rem" }}
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted">Loading driver details...</p>
      </div>
    );
  }

  if (error || !driver) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
        <div className="alert alert-danger p-4 rounded-4 shadow-sm text-center">
          <h4 className="mb-2">
            <i className="ri-error-warning-line me-2"></i>Driver Not Found
          </h4>
          <p>{error || "The requested driver could not be found."}</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => navigate("/driver")}
          >
            <i className="ri-arrow-left-line me-2"></i>Back to Driver List
          </button>
        </div>
      </div>
    );
  }
const currencySymbol = getCurrencySymbolFromPhone(driver?.user?.phone_no);

  const address = driver.address;

  

  return (
    <>
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12 d-flex flex-wrap justify-content-between align-items-center border-bottom pb-3 mb-3 gap-2">
          <div className="d-flex align-items-center gap-2">
            <i className="ri-truck-line text-primary fs-2"></i>
            <h2 className="fw-bold mb-0">Driver Details</h2>
          </div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/driver">Drivers</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {driver.user?.name || "Driver Details"}
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="row g-4">
        {/* Profile Card */}
        <div className="col-12 col-lg-4">
          <div className="card rounded-4 shadow-sm border-0">
            <div className="card-body d-flex flex-column align-items-center text-center">
              <div
                className="position-relative mb-3"
                style={{ width: 140, height: 140 }}
              >
                <SafeImage
                  src={getProfileImageUrl(driver.user?.profile_image)}
                  className="img-fluid rounded-circle border border-3 border-white shadow"
                  alt={driver.user?.name}
                  style={{ width: 140, height: 140, objectFit: "cover" }}
                  fallbackSrc="/avatar-placeholder.png"
                />
                {/* Pencil Icon Button for Profile Image Edit */}
                <button
                  type="button"
                  className="btn btn-light btn-sm position-absolute d-flex align-items-center justify-content-center border shadow"
                  style={{
                    bottom: 0,
                    right: 0,
                    borderRadius: "50%",
                    width: 38,
                    height: 38,
                    zIndex: 2,
                  }}
                  onClick={() =>
                    !uploading &&
                    fileInputRef.current &&
                    fileInputRef.current.click()
                  }
                  title={uploading ? "Uploading..." : "Edit Profile Image"}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  disabled={uploading}
                >
                  {uploading ? (
                    <span
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    <i className="ri-pencil-line fs-5 text-secondary"></i>
                  )}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  disabled={uploading}
                />
              </div>
              <h3 className="fw-semibold mb-1">{driver.user?.name}</h3>
              <div className="text-muted mb-2">Driver</div>
              <div className="mb-2 small text-muted">
                <span className="me-2">
                  <i className="ri-hashtag"></i> {driver.driver_id}
                </span>
                <span>
                  <i className="ri-calendar-line"></i> Joined{" "}
                  {formatDate(driver.created_at)}
                </span>
              </div>
              <div className="d-flex gap-2 mt-2">
                <Link
                  to={`/driveraddorupdate/${driver.driver_id}`}
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="ri-pencil-line me-1"></i>Edit
                </Link>
                <Link to="/driver" className="btn btn-outline-secondary btn-sm">
                  <i className="ri-arrow-left-line me-1"></i>Back
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Details Section */}
        <div className="col-12 col-lg-8">
          <div className="card rounded-4 shadow-sm border-0">
            <div className="card-body">
              <div className="row g-4">
                {/* Personal Info */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Full Name</div>
                    <div className="fw-semibold">
                      {driver.user?.name || "N/A"}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Email</div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <a
                        href={`mailto:${driver.user?.email}`}
                        className="text-decoration-none"
                      >
                        {driver.user?.email || "N/A"}
                      </a>
                      <button
                        className="btn btn-link btn-sm p-0 text-primary"
                        title="Copy Email"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        onClick={() => {
                          navigator.clipboard.writeText(driver.user?.email);
                          toast.success("Email copied!");
                        }}
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Phone</div>
                    <div className="fw-semibold">
                      <a
                        href={`tel:${driver.user?.phone_no}`}
                        className="text-decoration-none"
                      >
                        {driver.user?.phone_no || "N/A"}
                      </a>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Vehicle Number</div>
                    <div className="fw-semibold">
                      {driver.vehicle_no || "N/A"}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Vehicle Type</div>
                    <div className="fw-semibold">
                      {driver.vehicle_type || "N/A"}
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="text-muted small mb-1">Address</div>
                    <div className="fw-semibold">
                      {driver.location || "N/A"}
                    </div>
                  </div>
                </div>
                {/* Account Info */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <div className="text-muted small mb-1">User ID</div>
                    <div className="fw-semibold">{driver.user?.user_id}</div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Account Status</div>
                    <div className="fw-semibold">
                      {getStatusBadge(driver.user?.status)}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Last Updated</div>
                    <div className="fw-semibold">
                      {formatDate(driver.updated_at)}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Account Created</div>
                    <div className="fw-semibold">
                      {formatDate(driver.user?.created_at)}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Driver ID</div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <span>#{driver.driver_id}</span>
                      <button
                        className="btn btn-link btn-sm p-0 text-primary"
                        title="Copy Driver ID"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        onClick={() => {
                          navigator.clipboard.writeText(driver.driver_id);
                          toast.success("Driver ID copied!");
                        }}
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>
                  {/* 🔹 Commission Type */}
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Commission Type</div>
                    <div className="fw-semibold text-capitalize">
                      {driver.commission_type || "N/A"}
                    </div>
                  </div>

                  {/* 🔹 Commission Value */}
                  <div className="mb-3">
                    <div className="text-muted small mb-1">
                      {driver.commission_type === "percentage"
                        ? "Commission (%)"
                        : driver.commission_type === "kmperamount"
                          ? "Per KM Commission"
                          : "Commission Amount"}
                    </div>
<div className="fw-semibold">
  {driver.commission !== null && driver.commission !== undefined ? (
    driver.commission_type === "percentage" ? (
      `${driver.commission}%`
    ) : driver.commission_type === "kmperamount" ? (
      driver.commission   
    ) : (
      <>
        <span className="me-1">{currencySymbol}</span>
        {driver.commission}
      </>
    )
  ) : (
    "N/A"
  )}
</div>

                  </div>

                  {/* 🔹 Base Price (only used for kmperamount, but DB-la irundha show pannuvom) */}
                  {/* 🔹 Base Price – only show when value exists */}
                  {driver.base_price !== null &&
                    driver.base_price !== undefined && (
                      <div className="mb-3">
                        <div className="text-muted small mb-1">Base Price</div>
                      <div className="fw-semibold">
  <span className="me-1">{currencySymbol}</span>
  {driver.base_price}
</div>

                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
